import { useState } from 'react';

import { ColumnNames, toColumnName } from '../types/ExtendEnums';
import type { HNVelgBehandlingsstedFrontend } from '../types/Resources';

import { createFilterConfig, useFilter, useFilterDrawer } from '@helsenorge/designsystem-react/components/Filter';
import { simpleConfig } from '@helsenorge/designsystem-react/components/Table';

import PageHeader from '@helsenorge/core-framework/page-header';
import PageLoader from '@helsenorge/core-framework/page-loader';
import { Table, Column, ExpandableRow } from '@helsenorge/data-table/components/table';
import { getSidetittelId } from '@helsenorge/framework-utils/hn-page';

import BehandlingsstedExpanded from './behandlingssted-expanded';
import BehandlingstilbudCell from './behandlingstilbud-cell';
import { type BehandlingsstederFilters, getInitialFilters } from './filters-helper';
import { getBehandlingsstederTabellData } from './table-helper';
import { useResources } from '../resources';
import BehandlingsstedCell from '../shared/behandlingssted-cell';
import Filter from '../shared/filter/filter';
import { useGetBehandlingsstederQuery } from '../store/api/velgBehandlingsstedApi';
import { getFilterResources } from '../utils/filters';
import { HN_VELG_BEHANDLINGSSTED_FRONTEND } from '../utils/resources-project';

import './styles.scss';

const Behandlingssteder: React.FC = () => {
  const { data, isLoading: loading, isError } = useGetBehandlingsstederQuery();
  const behandlingssteder = data?.behandlingssteder;

  const resources = useResources<HNVelgBehandlingsstedFrontend>(HN_VELG_BEHANDLINGSSTED_FRONTEND);

  const [sortColumnKey, setSortColumnKey] = useState(ColumnNames.Behandlingssted);
  const [sortDirIsAsc, setSortDirIsAsc] = useState(true);
  const behandlingsstederData = behandlingssteder ?? [];
  const filterData = resources ? getInitialFilters(resources, behandlingsstederData) : [];

  const onSortColumns = (key: string, asc: boolean): void => {
    setSortColumnKey(toColumnName(key));
    setSortDirIsAsc(asc);
  };

  const { filterOptions, getLabel } = createFilterConfig<BehandlingsstederFilters>({
    helseregion: {
      options: filterData[0]?.values.map(({ value, displayName }) => ({ value, label: displayName ?? value })),
      getLabel: x => x.label,
    },
    behandlingstilbud: {
      options: filterData[1]?.values.map(({ value, displayName }) => ({ value, label: displayName ?? value })),
      getLabel: x => x.label,
    },
    behandlingssted: {
      options: filterData[2]?.values.map(({ value, displayName }) => ({ value, label: displayName ?? value })),
      getLabel: x => x.label,
    },
    fritekst: { getLabel: value => String(value) },
  });

  const filter = useFilter(filterOptions);
  const drawer = useFilterDrawer<keyof BehandlingsstederFilters>();
  const tabellData = resources ? getBehandlingsstederTabellData(resources, behandlingsstederData, filter.filters) : [];

  const labels = {
    helseregion: resources?.filterHelseregion ?? '',
    behandlingstilbud: resources?.filterBehandlingstilbud ?? '',
    behandlingssted: resources?.filterBehandlingssted ?? '',
    fritekst: resources?.filterSearchText ?? '',
  };

  const filterResources = getFilterResources(resources);

  return (
    resources && (
      <PageLoader
        resources={resources}
        loading={loading}
        error={isError ? resources.tekniskFeilBehandlingssteder : undefined}
        labelId={getSidetittelId()}
      >
        <PageHeader title={resources.behandlingsstederTittel} hjelpetekstId="allebehandlingssteder" />
        <Filter
          filter={filter}
          drawer={drawer}
          getLabel={getLabel}
          resources={filterResources}
          resultCount={tabellData.length}
          resultLabel={resources.behandlingsstederFilterUnit}
          title={resources.filterTitle}
          categories={[
            { key: 'helseregion', title: labels.helseregion },
            { key: 'behandlingstilbud', title: labels.behandlingstilbud },
            { key: 'behandlingssted', title: labels.behandlingssted },
          ]}
          filterData={filterData}
        />
        <Table
          id="behandlingssteder-table"
          data={tabellData}
          tableClassNames="vbs-behandlingssteder__behandlingssteder-tabell"
          initialSortColumnKey={sortColumnKey}
          initialSortDirIsAsc={sortDirIsAsc}
          sortColumnKey={sortColumnKey}
          sortDirIsAsc={sortDirIsAsc}
          onSortColumn={onSortColumns}
          showMoreButtonClasses="vbs-main__hent-flere"
          showMorePageSize={20}
          showMoreButtonText={resources.hentFlere}
          highlightText={filter.filters.fritekst}
          emptyContentText={resources.tabellIngenTreff}
          caption={resources.behandlingsstederTabellCaption}
          useNumericSort
          noAnchorLinkWrapper
          breakpointConfig={simpleConfig}
        >
          <Column
            name={ColumnNames.Behandlingssted}
            title={resources.behandlingsstederTabellBehandlingssted}
            sortable
            cell={<BehandlingsstedCell />}
          />
          <Column
            name={ColumnNames.Behandlingstilbud}
            title={resources.behandlingsstederTabellBehandlingstilbud}
            sortable
            cell={<BehandlingstilbudCell />}
          />
          <Column name={ColumnNames.Ventetid} title={resources.behandlingsstederTabellVentetid} sortable />

          <ExpandableRow open tableId="behandlingssteder-table">
            <BehandlingsstedExpanded />
          </ExpandableRow>
        </Table>
      </PageLoader>
    )
  );
};

export default Behandlingssteder;
